import React from 'react';
import { debounce } from 'lodash';

import {
  collapseSiblingRe,
  endOfMatchMark,
  matchMarkGroupsRe,
  startOfMatchMark,
} from '@neptune/shared/common-util';
import { bemBlock, Text, TextProps } from '@neptune/shared/venus-ui';

import { TextWithMatching } from '../../TextWithMatching';

import { useMeasureTextWidth } from './use-measure-text-width';

import './MiddleEllipsis.less';

type MiddleEllipsisProps = {
  text: string;
  id?: string;
  match?: string;
  expandable?: boolean;
  'data-role'?: string;
  expanded?: boolean;
  onTruncationChange?: (isTruncated: boolean) => void;
} & Pick<
  TextProps<{}>,
  'size' | 'className' | 'fontFamily' | 'fontWeight' | 'lineHeight' | 'color' | 'style'
>;

const block = bemBlock('middle-ellipsis');

const markCharRe = new RegExp(`[${endOfMatchMark}${startOfMatchMark}]`);

function getMarkedParts(firstPart: string, secondPart: string) {
  const hasUnfinishedMarks = secondPart.match(markCharRe)?.[0] === endOfMatchMark;
  return {
    firstPartWithEndMark: `${firstPart}${hasUnfinishedMarks ? endOfMatchMark : ''}`,
    secondPartWithStartMark: `${hasUnfinishedMarks ? startOfMatchMark : ''}${secondPart}`,
  };
}

function createHighlightedTextFragment(textToDisplay: string) {
  const fragment = document.createDocumentFragment();

  [...textToDisplay.replace(collapseSiblingRe, '').matchAll(matchMarkGroupsRe)]
    .filter(([, normal, matching]) => normal || matching)
    .forEach(([, normal, matching]) => {
      const span = document.createElement('span');
      span.textContent = normal;

      if (matching) {
        const mark = document.createElement('mark');
        mark.textContent = matching;
        span.appendChild(mark);
      }

      fragment.appendChild(span);
    });

  return fragment;
}

function updateElement(
  element: HTMLSpanElement,
  hasMatchHighlight: boolean,
  textToDisplay: string,
): void {
  if (hasMatchHighlight) {
    element.replaceChildren(createHighlightedTextFragment(textToDisplay));
  } else {
    element.textContent = textToDisplay;
  }
}

export const MiddleEllipsis = ({
  text,
  id,
  match,
  size,
  color,
  expandable,
  className,
  fontFamily,
  fontWeight,
  lineHeight,
  'data-role': dataRole,
  style,
  expanded,
  onTruncationChange,
}: MiddleEllipsisProps) => {
  const [isHovered, setIsHovered] = React.useState<boolean>(false);
  const textToDisplay = match ?? text ?? '';

  const isFullTextVisible = expandable === true ? expanded === true || isHovered : false;

  const textContainerRef = React.useRef<HTMLDivElement>(null);

  const hasMatchHighlight = !!match;

  const setupMeasureTextWidth = useMeasureTextWidth();

  const isTruncated = React.useRef<boolean | undefined>(undefined);

  const updateTruncationState = React.useCallback(
    (newTruncatedState: boolean): void => {
      const hasStateChanged = isTruncated.current !== newTruncatedState;

      if (hasStateChanged) {
        isTruncated.current = newTruncatedState;
        onTruncationChange?.(newTruncatedState);
      }
    },
    [onTruncationChange],
  );

  const updateEllipsisText = React.useCallback(
    (ref: React.RefObject<HTMLDivElement>): void => {
      if (!ref.current) {
        return;
      }

      const container = ref.current.parentElement;
      const textContainer = ref.current;
      const truncatedStartElement = ref.current.children[1] as HTMLSpanElement;
      const truncatedEndElement = ref.current.children[2] as HTMLSpanElement;

      if (!textContainer || !container || !truncatedStartElement || !truncatedEndElement) {
        return;
      }

      updateElement(truncatedStartElement, hasMatchHighlight, textToDisplay);
      truncatedStartElement.style.width = 'auto';
      truncatedEndElement.textContent = '';

      if (isFullTextVisible) {
        updateTruncationState(false);
        return;
      }

      const containerWidth =
        container.offsetWidth > textContainer.offsetWidth
          ? textContainer.offsetWidth
          : container.offsetWidth;

      const textWidth = truncatedStartElement.offsetWidth;

      if (textWidth > 0 && textWidth > containerWidth) {
        const halfContainerWidth = Math.floor(containerWidth / 2);

        const averageLetterWidth = textWidth / textToDisplay.length;

        const measureTextWidth = setupMeasureTextWidth(ref);
        const ellipsisWidth = measureTextWidth('...');

        const lettersToMove = Math.floor((halfContainerWidth - ellipsisWidth) / averageLetterWidth);

        const firstPart = textToDisplay.slice(0, textToDisplay.length - lettersToMove);
        const secondPart = textToDisplay.slice(-lettersToMove);

        const { firstPartWithEndMark, secondPartWithStartMark } = getMarkedParts(
          firstPart,
          secondPart,
        );

        updateElement(
          truncatedStartElement,
          hasMatchHighlight,
          hasMatchHighlight ? firstPartWithEndMark : firstPart,
        );

        updateElement(
          truncatedEndElement,
          hasMatchHighlight,
          hasMatchHighlight ? secondPartWithStartMark : secondPart,
        );

        truncatedStartElement.style.width = `${halfContainerWidth}px`;

        updateTruncationState(true);
      } else {
        updateTruncationState(false);
      }
    },
    [
      hasMatchHighlight,
      setupMeasureTextWidth,
      textToDisplay,
      isFullTextVisible,
      updateTruncationState,
    ],
  );

  const updateEllipsisTextDebounced = React.useMemo(
    () =>
      debounce((ref: React.RefObject<HTMLDivElement>) => {
        updateEllipsisText(ref);
      }, 200),
    [updateEllipsisText],
  );

  React.useEffect(() => {
    updateEllipsisTextDebounced(textContainerRef);
  }, [updateEllipsisTextDebounced, isFullTextVisible, textToDisplay]);

  React.useEffect(() => {
    let firstUpdate = true;

    const resizeObserver = new ResizeObserver(() => {
      if (textContainerRef.current) {
        if (firstUpdate) {
          updateEllipsisText(textContainerRef);
          firstUpdate = false;
        } else {
          updateEllipsisTextDebounced(textContainerRef);
        }
      }
    });

    if (textContainerRef.current) {
      resizeObserver.observe(textContainerRef.current);

      // Observe all parent elements up to document body for width changes
      let parent = textContainerRef.current.parentElement;

      while (parent && parent !== document.body) {
        resizeObserver.observe(parent);
        parent = parent.parentElement;
      }
    }

    return () => {
      resizeObserver.disconnect();
      updateEllipsisTextDebounced.cancel();
    };
  }, [updateEllipsisTextDebounced, updateEllipsisText]);

  return (
    <Text
      className={block({
        extra: className,
      })}
      title={text}
      fontWeight={fontWeight}
      fontFamily={fontFamily}
      lineHeight={lineHeight}
      size={size}
      color={color}
      data-role={dataRole}
      onPointerEnter={expandable ? () => setIsHovered(true) : undefined}
      onPointerLeave={expandable ? () => setIsHovered(false) : undefined}
      style={style}
      elementRef={textContainerRef}
    >
      <div
        id={id}
        data-role="middle-ellipsis-full-text"
        className={block({
          element: 'full-text',
        })}
      >
        {hasMatchHighlight ? <TextWithMatching text={textToDisplay} /> : textToDisplay}
      </div>

      <Text
        data-role="middle-ellipsis-truncated-start"
        className={block({
          element: 'truncated-start',
        })}
      />

      <Text
        data-role="middle-ellipsis-truncated-end"
        className={block({
          element: 'truncated-end',
        })}
      />
    </Text>
  );
};
